// 메시지 목록 — 바닥 고정(stick) 스크롤. chat 프로젝트 web/MessageList.tsx 의 방식을 옮겼다:
// 사용자가 위로 올려 읽는 중이면 새 메시지가 와도 끌어내리지 않고, 바닥에 있을 때만 따라간다.
// 스트리밍 중 높이가 계속 변하므로 ResizeObserver 로 같이 붙인다.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FiAlertTriangle, FiArrowDown, FiChevronDown, FiChevronRight, FiFileText, FiImage, FiTool } from 'react-icons/fi';
import { getAttachmentsFor } from '../../services/assistantClient';
import SqlBlock from './SqlBlock';
import QuestionCard from './QuestionCard';
import { topSources, childCount } from '../../utils/sourceTree';
import styles from './AssistantPanel.module.css';

const STICK_THRESHOLD = 40;

/** 휠·터치·키 입력 뒤 이 시간 안의 스크롤만 "사람이 움직인 것" 으로 본다. */
const INTENT_WINDOW = 250;

/** 이 줄 수(또는 글자 수)를 넘는 사용자 메시지는 접어서 보여 준다. */
const COLLAPSE_LINES = 12;
const COLLAPSE_CHARS = 800;

/**
 * 사용자가 보낸 말풍선.
 *
 * 1,000줄짜리 쿼리를 붙여 넣고 "이 부분 고쳐줘" 하는 경우가 흔한데, 그대로 그리면 대화창이
 * 그 쿼리로 뒤덮여 앞뒤 맥락을 못 본다. 길면 접고 **몇 줄인지 알려 준 뒤** 펼치게 한다.
 *
 * 접는 기준을 줄 수와 글자 수 **둘 다** 보는 이유: 한 줄로 이어 붙인 긴 쿼리는 줄 수로는
 * 안 걸린다.
 */
/** 그 메시지와 함께 보낸 첨부. 채팅의 첨부처럼 **메시지에 붙어** 보인다. */
function AttachmentChips({ messageId }) {
    const { t } = useTranslation();
    const list = getAttachmentsFor(messageId);
    if (!list) return null;
    return (
        <div className={styles.bubbleAttach}>
            {/* 문서에서 꺼낸 그림은 따로 세우지 않는다 — 문서 칩에 장 수만(첨부 줄과 같은 규칙, utils/sourceTree) */}
            {topSources(list).map(a => (
                <span key={a.id} className={styles.bubbleAttachChip} title={a.filename}>
                    {a.kind === 'image' ? <FiImage size={10} /> : <FiFileText size={10} />}
                    <span className={styles.sourceName}>{a.filename}</span>
                    {childCount(list, a.id) ? <span className={styles.sourceInner}>{t('assistant:case.innerImages', { count: childCount(list, a.id) })}</span> : null}
                </span>
            ))}
        </div>
    );
}
AttachmentChips.propTypes = { messageId: PropTypes.string };

function UserBubble({ id, content }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const text = String(content || '');
    const lines = text.split('\n');
    const long = lines.length > COLLAPSE_LINES || text.length > COLLAPSE_CHARS;

    if (!long) {
        return (
            <div className={`${styles.bubble} ${styles.user}`}>
                <AttachmentChips messageId={id} />
                {text}
            </div>
        );
    }

    const head = lines.slice(0, COLLAPSE_LINES).join('\n').slice(0, COLLAPSE_CHARS);
    return (
        <div className={`${styles.bubble} ${styles.user}`}>
            <AttachmentChips messageId={id} />
            {open ? text : `${head}\n…`}
            <button type="button" className={styles.userMore} onClick={() => setOpen(!open)}>
                {open
                    ? t('assistant:msg.collapse')
                    : t('assistant:msg.expand', { lines: lines.length, chars: text.length })}
            </button>
        </div>
    );
}

UserBubble.propTypes = { id: PropTypes.string, content: PropTypes.string };

/** 접히는 도구 호출 한 줄. */
function ToolRow({ msg }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const input = msg.input ? JSON.stringify(msg.input) : '';
    return (
        <div className={styles.tool}>
            <button type="button" className={styles.toolHead} onClick={() => setOpen(o => !o)}>
                {open ? <FiChevronDown size={11} /> : <FiChevronRight size={11} />}
                <FiTool size={11} />
                <span className={`${styles.toolName} ${msg.isError ? styles.toolFail : ''}`}>{msg.tool}</span>
                <span>{input.length > 60 ? `${input.slice(0, 60)}…` : input}</span>
            </button>
            {open && (
                <pre className={styles.toolBody}>
                    {input ? `${t('assistant:tool.input')}: ${input}\n\n` : ''}
                    {msg.result ? `${t('assistant:tool.result')}: ${msg.result}` : t('assistant:tool.running')}
                </pre>
            )}
        </div>
    );
}
ToolRow.propTypes = { msg: PropTypes.object.isRequired };

/** 공백을 무시한 SQL 비교용 키 — 제안 카드와 본문 코드펜스의 중복 판정에 쓴다. */
const sqlKey = (s) => String(s).replace(/\s+/g, ' ').replace(/;+\s*$/, '').trim().toUpperCase();

/** 에이전트 본문 — ```sql 블록만 SqlBlock 으로 바꿔 그린다. */
function AssistantText({ text, actions, proposed }) {
    return (
        <div className={`${styles.bubble} ${styles.assistant}`}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    // react-markdown 9+ 는 code 에 `inline` 을 주지 않는다 — 그래서 본문 속 `COUNT` 까지 전부 블록(pre)으로
                    // 그려 한 줄 문장이 여러 줄로 흩어졌다(신고 2026-10-10). 블록은 pre 가 받고, code 는 늘 인라인이다.
                    pre({ children }) {
                        const child = React.Children.toArray(children)[0];
                        const cp = (child && child.props) || {};
                        const lang = /language-(\w+)/.exec(cp.className || '');
                        const body = String(cp.children == null ? '' : cp.children).replace(/\n$/, '');
                        if (lang && lang[1].toLowerCase() === 'sql') {
                            // 모델은 propose_sql 로 낸 SQL 을 본문에도 한 번 더 적는다.
                            // 위에 이미 실행 버튼이 달린 카드가 있으므로 여기서는 그리지 않는다.
                            if (proposed && proposed.has(sqlKey(body))) return null;
                            return <SqlBlock sql={body} {...actions} />;
                        }
                        return <pre className={styles.sqlCode}><code className={cp.className}>{body}</code></pre>;
                    },
                    code({ className, children }) {
                        return <code className={className}>{children}</code>;
                    },
                }}
            >
                {text}
            </ReactMarkdown>
        </div>
    );
}
AssistantText.propTypes = { text: PropTypes.string.isRequired, actions: PropTypes.object.isRequired, proposed: PropTypes.instanceOf(Set) };

/** 도움말 안내 — 마크다운 본문(```sql 은 실행 카드) + 선택 버튼. 버튼은 누르면 그 글이 메시지로 간다. */
function HelpBubble({ msg, actions, onOption }) {
    return (
        <div className={styles.helpWrap}>
            {msg.content ? <AssistantText text={msg.content} actions={actions} /> : null}
            {msg.options && msg.options.length > 0 && !msg.used ? (
                <div className={styles.helpOptions}>
                    {msg.options.map(o => (
                        <button key={o} type="button" className={styles.helpOptionBtn} onClick={() => onOption(o, msg.id)}>{o}</button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}
HelpBubble.propTypes = { msg: PropTypes.object.isRequired, actions: PropTypes.object.isRequired, onOption: PropTypes.func.isRequired };

/**
 * 서버 메시지(공통 훅)와 로컬 메시지(도움말·제안 버튼)를 한 줄로 합친다.
 * 로컬 메시지의 anchor = 만들 때의 서버 메시지 수. 그 자리에 끼운다 — 뒤에 서버 턴이 이어져도
 * 도움말 대화가 맨 아래로 밀려 내려가지 않는다.
 */
function merge(messages, extras) {
    if (!extras || extras.length === 0) return messages;
    const out = [];
    for (let i = 0; i < messages.length; i++) {
        for (const x of extras) if (x.anchor === i) out.push(x);
        out.push(messages[i]);
    }
    for (const x of extras) if (x.anchor >= messages.length) out.push(x);
    return out;
}

function MessageList({ messages, extras, streamText, busy, actions, onAnswer, onExample, onOption }) {
    const { t } = useTranslation();
    const scrollRef = useRef(null);
    const innerRef = useRef(null);
    const [stick, setStick] = useState(true);

    // 따라가기를 멈추는 신호는 **사람의 입력**뿐이다.
    //
    // 전에는 "scrollTop 이 줄면 사람이 올린 것" 으로 봤다. 그런데 **내용이 줄어들 때도**
    // 브라우저가 scrollTop 을 잘라 내려 똑같은 모양이 된다 — 스트리밍 말풍선이 최종 메시지로
    // 바뀌는 순간, "생각 중" 이 사라지는 순간, 긴 질문을 다시 접는 순간이 그렇다.
    // 그래서 그때마다 따라가기가 조용히 꺼졌다.
    //
    // 이제 휠·터치·키·스크롤바 드래그가 **직전에 있었을 때만** 멈춘다. 내용 변화는 아무리
    // 요동쳐도 이 판단에 끼어들 수 없다.
    const intentAtRef = useRef(0);
    const noteIntent = () => { intentAtRef.current = Date.now(); };

    const onScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD) {
            setStick(true);                       // 바닥으로 되돌아오면 다시 따라간다
            return;
        }
        if (Date.now() - intentAtRef.current < INTENT_WINDOW) setStick(false);
    };

    const toBottom = () => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    };

    // 마지막 메시지의 길이만 보면 **도구 결과가 뒤늦게 채워지거나 중간 메시지가 자라는 것**을
    // 놓친다. 메시지는 많아야 수십 개이므로 전부 합쳐 한 숫자로 만든다.
    const all = useMemo(() => merge(messages, extras), [messages, extras]);
    const growth = useMemo(() => all.reduce(
        (n, m) => n + String(m.content || '').length + String(m.result || '').length + (m.resolved ? 1 : 0) + (m.used ? 1 : 0),
        all.length,
    ), [all]);

    // 방금 보냈으면 무조건 따라간다 — 위를 읽던 중이었더라도 자기 질문의 답은 봐야 한다.
    const lastRole = messages.length ? messages[messages.length - 1].role : null;
    useEffect(() => {
        if (lastRole === 'user') setStick(true);
    }, [messages.length, lastRole]);

    useLayoutEffect(() => {
        if (stick) toBottom();
    }, [growth, streamText, busy, stick]);

    // 마크다운·표·이미지는 위 효과가 끝난 **뒤에** 자리를 잡는다. 그 높이 변화까지 따라간다.
    useEffect(() => {
        const inner = innerRef.current;
        if (!inner || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => { if (stick) toBottom(); });
        ro.observe(inner);
        return () => ro.disconnect();
    }, [stick]);

    const examples = [t('assistant:example.one'), t('assistant:example.two'), t('assistant:example.three')];
    // 제안 카드로 이미 그린 SQL — 본문 코드펜스에서 같은 것이 또 나오면 건너뛴다.
    const proposed = useMemo(
        () => new Set(messages.filter(m => m.kind === 'sql_proposal' && m.sql).map(m => sqlKey(m.sql))),
        [messages],
    );

    return (
        <div className={styles.listWrap}>
            <div
                className={styles.list}
                ref={scrollRef}
                onScroll={onScroll}
                onWheel={noteIntent}
                onTouchMove={noteIntent}
                onKeyDown={noteIntent}
                onMouseDown={noteIntent}
            >
                <div ref={innerRef} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {all.length === 0 && !streamText && (
                        <div className={styles.empty}>
                            <strong>{t('assistant:empty.title')}</strong>
                            {t('assistant:empty.body')}
                            {examples.map(ex => (
                                <button key={ex} type="button" className={styles.exampleBtn} onClick={() => onExample(ex)}>{ex}</button>
                            ))}
                        </div>
                    )}

                    {all.map((m) => {
                        if (m.kind === 'help') {
                            if (m.role === 'user') return <UserBubble key={m.id} id={m.id} content={m.content} />;
                            return <HelpBubble key={m.id} msg={m} actions={actions} onOption={onOption} />;
                        }
                        if (m.kind === 'tool_call') return <ToolRow key={m.id} msg={m} />;
                        if (m.kind === 'question') return <QuestionCard key={m.id} question={m.question} resolved={m.resolved} onAnswer={onAnswer} />;
                        if (m.kind === 'sql_proposal') return <SqlBlock key={m.id} sql={m.sql} rationale={m.rationale} {...actions} />;
                        if (m.kind === 'error') {
                            return (
                                <div key={m.id} className={`${styles.bubble} ${styles.errorBubble}`}>
                                    <FiAlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
                                    <span>{m.content}</span>
                                </div>
                            );
                        }
                        if (m.role === 'user') return <UserBubble key={m.id} id={m.id} content={m.content} />;
                        return <AssistantText key={m.id} text={m.content || ''} actions={actions} proposed={proposed} />;
                    })}

                    {streamText ? (
                        <div className={`${styles.bubble} ${styles.assistant} ${styles.streaming}`}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamText}</ReactMarkdown>
                        </div>
                    ) : null}
                    {busy && !streamText ? (
                        <div className={`${styles.bubble} ${styles.assistant} ${styles.streaming}`}>
                            {t('assistant:thinking')}<span className={styles.caret} />
                        </div>
                    ) : null}
                </div>
            </div>
            {!stick && (
                <button
                    type="button"
                    className={styles.toBottom}
                    onClick={() => { setStick(true); toBottom(); }}
                >
                    <FiArrowDown size={12} />
                    {t('assistant:msg.toBottom')}
                </button>
            )}
        </div>
    );
}

MessageList.propTypes = {
    messages: PropTypes.array.isRequired,
    extras: PropTypes.array,
    onOption: PropTypes.func,
    streamText: PropTypes.string,
    busy: PropTypes.bool,
    actions: PropTypes.object.isRequired,
    onAnswer: PropTypes.func.isRequired,
    onExample: PropTypes.func.isRequired,
};

export default MessageList;
